<script setup>
import { ref, computed, watch } from "vue";
import { useGlobeStore } from "@stores/globe";
import { useDataStore } from "@stores/data";

const globeStore = useGlobeStore();
const dataStore = useDataStore();

const lastActive = ref({ country: null, continent: null });

watch(
    [
        () => globeStore.selectedCountry ?? globeStore.hoveredCountry,
        () => globeStore.selectedContinent ?? globeStore.hoveredContinent,
    ],
    ([country, continent]) => {
        if (!country) return;
        lastActive.value = { country, continent };
    },
    { immediate: true },
);

const activeCountry = computed(() => lastActive.value.country);
const activeContinent = computed(() => lastActive.value.continent);

const continentLabel = computed(
    () => dataStore.continentLabels?.[activeContinent.value] ?? "",
);

const activeCity = computed(() => {
    const country = activeCountry.value;
    if (!country) return null;
    return dataStore.pinnedCities.find(
        (c) => c.name === country || c.countryName === country,
    );
});

const description = computed(() => activeCity.value?.description ?? "");
</script>

<template>
    <Transition name="fade" appear>
        <div
            v-if="activeCountry"
            class="vote-country-info bg-marine-800 border-marine-500 flex flex-col justify-center gap-2 border-b p-6"
        >
            <p class="body-sm leading-[0.9] font-bold tracking-tight uppercase">
                {{ continentLabel }}
            </p>
            <p
                class="country-name h3 leading-[0.85] font-extrabold tracking-tight text-yellow-400 uppercase"
            >
                {{ activeCountry }}
            </p>
            <p v-if="description" class="body-sm mt-4 leading-[1.4]">
                {{ description }}
            </p>
        </div>
    </Transition>
</template>

<style scoped>
.vote-country-info {
    grid-column: -11 / -6;
    grid-row: 2;
    z-index: 2;
    align-self: start;
}

@media (max-width: 1024px) {
    .vote-country-info {
        display: none;
    }
}

.country-name {
    text-shadow: 0 0.1875em 0 var(--marine-base, #19233b);
    -webkit-text-stroke-width: 0.125em;
    -webkit-text-stroke-color: var(--Marine-600, #19233b);
    paint-order: stroke fill;
}
</style>
